import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';

interface Holiday {
  id: string;
  holiday_date: string;
  holiday_name: string;
  holiday_type: string;
  is_paid: boolean;
  calendar_name: string;
}

const EMPTY_FORM = {
  calendarId: '',
  holidayDate: '',
  holidayName: '',
  holidayType: 'PUBLIC_HOLIDAY',
  isPaid: true,
};

const fetchCalendarOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/lms/holiday-calendars');
  const calendars = res.data.calendars || [];
  const filtered = search
    ? calendars.filter((c: any) => c.name.toLowerCase().includes(search.toLowerCase()))
    : calendars;
  return filtered.map((c: any) => ({ value: c.id, label: `${c.name} (${c.calendar_year})` }));
};

export default function Holidays() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const year = new Date().getFullYear();
      const response = await api.get('/lms/holidays', { params: { year } });
      setHolidays(response.data.holidays || []);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (holiday: Holiday) => {
    setEditingId(holiday.id);
    setFormData({
      calendarId: '',
      holidayDate: holiday.holiday_date.slice(0, 10),
      holidayName: holiday.holiday_name,
      holidayType: holiday.holiday_type,
      isPaid: holiday.is_paid,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/lms/holidays/${editingId}`, formData);
      } else {
        await api.post('/lms/holidays', formData);
      }
      setFormData(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchHolidays();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save holiday');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/lms/holidays/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchHolidays();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove holiday');
    }
  };

  const columns: Column<Holiday>[] = [
    {
      key: 'holiday_date',
      header: 'Date',
      sortable: true,
      render: (row) => new Date(row.holiday_date).toLocaleDateString(),
    },
    { key: 'holiday_name', header: 'Holiday Name' },
    { key: 'calendar_name', header: 'Calendar' },
    { key: 'holiday_type', header: 'Type', render: (row) => <StatusBadge status={row.holiday_type} /> },
    {
      key: 'is_paid',
      header: 'Paid',
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${row.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.is_paid ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline text-sm">
            Remove
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Holiday Calendar</h1>
        <Button onClick={openCreateForm}>Add Holiday</Button>
      </div>

      <Table columns={columns} rows={holidays} loading={loading} emptyMessage="No holidays yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Holiday' : 'Add Holiday'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingId && (
            <LookupSelect
              label="Calendar"
              value={formData.calendarId}
              onChange={(value) => setFormData({ ...formData, calendarId: value })}
              fetchOptions={fetchCalendarOptions}
              placeholder="Search calendar..."
              required
            />
          )}
          <input
            type="date"
            value={formData.holidayDate}
            onChange={(e) => setFormData({ ...formData, holidayDate: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg"
            required
          />
          <input
            type="text"
            placeholder="Holiday Name"
            value={formData.holidayName}
            onChange={(e) => setFormData({ ...formData, holidayName: e.target.value })}
            className="w-full px-4 py-2 border rounded-lg"
            required
          />
          <Select
            value={formData.holidayType}
            onChange={(e) => setFormData({ ...formData, holidayType: e.target.value })}
            options={[
              { value: 'PUBLIC_HOLIDAY', label: 'Public Holiday' },
              { value: 'WEEKLY_OFF', label: 'Weekly Off' },
              { value: 'FESTIVAL', label: 'Festival' },
              { value: 'COMPANY_EVENT', label: 'Company Event' },
            ]}
          />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isPaid}
              onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Is Paid</span>
          </label>
          <Button type="submit" className="w-full">
            {editingId ? 'Save Changes' : 'Add Holiday'}
          </Button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove Holiday"
        message={`Remove "${deleteTarget?.holiday_name}" from the calendar?`}
        confirmLabel="Remove"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
